import type { OoxmlParagraphMeta, ParagraphFidelityStats } from "../types";
import { collectImportBlockElements } from "./collectBlocks";

function hasInlineAlign(el: HTMLElement): boolean {
  const align = el.style.textAlign?.toLowerCase();
  return !!align && align !== "start";
}

function hasIndent(el: HTMLElement): boolean {
  return !!(
    el.style.marginLeft ||
    el.style.marginRight ||
    el.style.textIndent
  );
}

function hasSpacing(el: HTMLElement): boolean {
  return !!(el.style.marginTop || el.style.marginBottom);
}

function applyMetaToElement(el: HTMLElement, meta: OoxmlParagraphMeta): {
  alignment: boolean;
  indent: boolean;
  spacing: boolean;
  lineHeight: boolean;
} {
  const applied = {
    alignment: false,
    indent: false,
    spacing: false,
    lineHeight: false,
  };

  if (meta.alignment && !hasInlineAlign(el)) {
    el.style.textAlign = meta.alignment;
    applied.alignment = true;
  }

  if (meta.marginLeftPx != null && !el.style.marginLeft) {
    el.style.marginLeft = `${meta.marginLeftPx}px`;
    applied.indent = true;
  }
  if (meta.marginRightPx != null && !el.style.marginRight) {
    el.style.marginRight = `${meta.marginRightPx}px`;
    applied.indent = true;
  }
  if (meta.textIndentPx != null && !el.style.textIndent) {
    el.style.textIndent = `${meta.textIndentPx}px`;
    applied.indent = true;
  }

  if (meta.marginTopPx != null && !el.style.marginTop) {
    el.style.marginTop = `${meta.marginTopPx}px`;
    applied.spacing = true;
  }
  if (meta.marginBottomPx != null && !el.style.marginBottom) {
    el.style.marginBottom = `${meta.marginBottomPx}px`;
    applied.spacing = true;
  }

  if (meta.lineHeight && !el.style.lineHeight) {
    el.style.lineHeight = meta.lineHeight;
    applied.lineHeight = true;
  }

  return applied;
}

/**
 * Merge OOXML paragraph metadata onto mammoth HTML by document-order index.
 * Runs before sanitize so empty mammoth paragraphs still align with OOXML rows.
 */
export function applyParagraphMetaToHtml(
  html: string,
  metaList: OoxmlParagraphMeta[],
): { html: string; stats: ParagraphFidelityStats } {
  const stats: ParagraphFidelityStats = {
    paragraphsInDocx: metaList.length,
    paragraphsInHtml: 0,
    paragraphsEnriched: 0,
    appliedAlignment: 0,
    appliedIndent: 0,
    appliedSpacing: 0,
    appliedLineHeight: 0,
  };

  if (!html.trim() || metaList.length === 0) {
    return { html, stats };
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<div id="meta-root">${html}</div>`,
    "text/html",
  );
  const root = doc.getElementById("meta-root");
  if (!root) return { html, stats };

  const blocks = collectImportBlockElements(root);
  stats.paragraphsInHtml = blocks.length;

  const limit = Math.min(blocks.length, metaList.length);
  for (let i = 0; i < limit; i++) {
    const el = blocks[i];
    const meta = metaList[i];
    if (!meta) continue;

    const hasAny =
      meta.alignment ||
      meta.marginLeftPx != null ||
      meta.marginRightPx != null ||
      meta.textIndentPx != null ||
      meta.marginTopPx != null ||
      meta.marginBottomPx != null ||
      meta.lineHeight;

    if (!hasAny) continue;

    const applied = applyMetaToElement(el, meta);
    if (
      applied.alignment ||
      applied.indent ||
      applied.spacing ||
      applied.lineHeight
    ) {
      stats.paragraphsEnriched++;
    }
    if (applied.alignment) stats.appliedAlignment++;
    if (applied.indent) stats.appliedIndent++;
    if (applied.spacing) stats.appliedSpacing++;
    if (applied.lineHeight) stats.appliedLineHeight++;
  }

  return { html: root.innerHTML.trim(), stats };
}
